import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import api from '../../../const/axios'

type Tournament = { id: number; name: string; added: string; isLeague?: boolean; submissionStatus?: string | null }
type TeamPlayer = { id: number; name: string }
type Team = { id: number; name: string; city: string; players: TeamPlayer[] }
type ParsedRow = {
  teamName: string
  answers: any[]
  total: number
  teamQuestionRating?: number
  suggestedTeamId?: number
  suggestedTeamName?: string
  suggestedConfidence?: number
}
type LeagueStanding = { teamId: number; teamName: string; totalPoints: number; rank: number }

const OrganizerPortal: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [selectedTournamentId, setSelectedTournamentId] = useState<number | undefined>()
  const [newTournamentName, setNewTournamentName] = useState('')
  const [newTournamentIsLeague, setNewTournamentIsLeague] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([])
  const [questionCount, setQuestionCount] = useState(0)
  const [questionRatings, setQuestionRatings] = useState<(number | null)[]>([])
  const [rowTeamMap, setRowTeamMap] = useState<Record<number, number | undefined>>({})
  const [rowPlayersMap, setRowPlayersMap] = useState<Record<number, number[]>>({})
  const [eventDate, setEventDate] = useState('')
  const [ageTagsText, setAgeTagsText] = useState('adult')

  const [leaguePointsText, setLeaguePointsText] = useState('20,17,15,14,13,12,11,10,9,8,7,6,5,4,3,2,1')
  const [leagueRoundNumber, setLeagueRoundNumber] = useState(1)
  const [leagueRoundDate, setLeagueRoundDate] = useState('')
  const [leagueStandings, setLeagueStandings] = useState<LeagueStanding[]>([])
  const [leagueLineupMap, setLeagueLineupMap] = useState<Record<number, number[]>>({})

  const resetAlerts = () => {
    setMessage('')
    setError('')
  }

  const selectedTournament = useMemo(
    () => tournaments.find((t) => t.id === selectedTournamentId),
    [tournaments, selectedTournamentId]
  )
  const isLeagueSelected = !!selectedTournament?.isLeague

  const teamById = useMemo(() => {
    const map: Record<number, Team> = {}
    teams.forEach((t) => (map[t.id] = t))
    return map
  }, [teams])

  const loadLeagueConfigAndStandings = useCallback(async () => {
    if (!selectedTournamentId || !isLeagueSelected) return
    try {
      const [cfgRes, stRes] = await Promise.all([
        api.get(`/ratings/leagues/${selectedTournamentId}/config`),
        api.get(`/ratings/leagues/${selectedTournamentId}/standings`),
      ])
      if (cfgRes.data?.positionPoints?.length) {
        setLeaguePointsText((cfgRes.data.positionPoints as number[]).join(','))
      }
      const standings: LeagueStanding[] = stRes.data || []
      setLeagueStandings(standings)
      const nextLineups: Record<number, number[]> = {}
      standings.forEach((s) => {
        nextLineups[s.teamId] = (teamById[s.teamId]?.players || []).slice(0, 6).map((p) => p.id)
      })
      setLeagueLineupMap(nextLineups)
    } catch (_) {
      setLeagueStandings([])
    }
  }, [selectedTournamentId, isLeagueSelected, teamById])

  const loadBootstrap = useCallback(async () => {
    try {
      const [tRes, teamRes] = await Promise.all([
        api.get('/ratings/organizer/tournaments'),
        api.get('/ratings/organizer/teams'),
      ])
      setTournaments(tRes.data || [])
      setTeams(teamRes.data || [])
      if (!selectedTournamentId && tRes.data?.length > 0) {
        setSelectedTournamentId(tRes.data[0].id)
      }
    } catch (err: any) {
      setError(err?.response?.data || 'Organizer məlumatları yüklənmədi.')
    }
  }, [selectedTournamentId])

  useEffect(() => {
    loadBootstrap()
  }, [loadBootstrap])

  useEffect(() => {
    loadLeagueConfigAndStandings()
  }, [loadLeagueConfigAndStandings])

  const handleCreateTournament = async () => {
    resetAlerts()
    if (!newTournamentName.trim()) {
      setError('Turnir adı boş ola bilməz.')
      return
    }

    try {
      const payload = { name: newTournamentName.trim(), isLeague: newTournamentIsLeague }
      const res = await api.post('/tournaments/create', JSON.stringify(payload))
      setMessage(`Turnir yaradıldı: ${res.data?.name || newTournamentName}`)
      setNewTournamentName('')
      setNewTournamentIsLeague(false)
      await loadBootstrap()
      if (res.data?.id) {
        setSelectedTournamentId(res.data.id)
      }
    } catch (err: any) {
      setError(err?.response?.data || 'Turnir yaradılmadı.')
    }
  }

  const handleSubmitForRating = async () => {
    resetAlerts()
    if (!selectedTournamentId) {
      setError('Turnir seçin.')
      return
    }

    try {
      await api.post(`/tournaments/${selectedTournamentId}/submit-for-rating`)
      setMessage('Turnir yoxlama üçün göndərildi.')
      await loadBootstrap()
    } catch (err: any) {
      setError(err?.response?.data || 'Turnir submit edilmədi.')
    }
  }

  const handleExcelUpload = async (file: File | null) => {
    resetAlerts()
    if (!file) return

    try {
      const fd = new FormData()
      fd.append('File', file)
      const res = await api.post('/ratings/excel/parse', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      const rows: ParsedRow[] = res.data?.rows || []
      setParsedRows(rows)
      setQuestionCount(res.data?.questionCount || 0)
      setQuestionRatings(res.data?.questionRatings || [])

      const initialMap: Record<number, number | undefined> = {}
      const initialPlayers: Record<number, number[]> = {}
      rows.forEach((r, i) => {
        initialMap[i] = r.suggestedTeamId
        if (r.suggestedTeamId && teamById[r.suggestedTeamId]) {
          initialPlayers[i] = teamById[r.suggestedTeamId].players.slice(0, 6).map((p) => p.id)
        } else {
          initialPlayers[i] = []
        }
      })
      setRowTeamMap(initialMap)
      setRowPlayersMap(initialPlayers)

      setMessage('Excel parse edildi. Team mapping yoxlayın.')
    } catch (err: any) {
      setError(err?.response?.data || 'Excel parse alınmadı.')
    }
  }

  const recalcPlayersByTeam = (rowIndex: number, teamId?: number) => {
    if (!teamId || !teamById[teamId]) {
      setRowPlayersMap((prev) => ({ ...prev, [rowIndex]: [] }))
      return
    }
    setRowPlayersMap((prev) => ({ ...prev, [rowIndex]: teamById[teamId].players.slice(0, 6).map((p) => p.id) }))
  }

  const buildRankMap = () => {
    const totals = parsedRows.map((row, idx) => ({ idx, total: Number(row.total || 0) }))
    totals.sort((a, b) => b.total - a.total)

    const ranks: Record<number, number> = {}
    let position = 1
    let i = 0
    while (i < totals.length) {
      const j = i
      const score = totals[i].total
      while (i < totals.length && totals[i].total === score) i++
      for (let k = j; k < i; k++) ranks[totals[k].idx] = position
      position += i - j
    }
    return ranks
  }

  const handleSaveLeagueConfig = async () => {
    resetAlerts()
    if (!selectedTournamentId) return
    const points = leaguePointsText
      .split(',')
      .map((x) => Number(x.trim()))
      .filter((x) => !Number.isNaN(x) && x >= 0)

    try {
      await api.post(`/ratings/leagues/${selectedTournamentId}/config`, { positionPoints: points })
      setMessage('League point cədvəli saxlanıldı.')
      await loadLeagueConfigAndStandings()
    } catch (err: any) {
      setError(err?.response?.data || 'League config saxlanmadı.')
    }
  }

  const handleSaveLeagueRound = async () => {
    resetAlerts()
    if (!selectedTournamentId) return
    if (parsedRows.length === 0) {
      setError('Əvvəlcə həftəlik Excel yükləyin.')
      return
    }

    const ranks = buildRankMap()
    const results: Array<{ teamId: number; position: number }> = []
    for (let i = 0; i < parsedRows.length; i++) {
      const teamId = rowTeamMap[i]
      if (!teamId) {
        setError(`${i + 1}-ci sətrin komandası map edilməyib.`)
        return
      }
      results.push({ teamId, position: ranks[i] })
    }

    try {
      await api.post(`/ratings/leagues/${selectedTournamentId}/rounds`, {
        roundNumber: leagueRoundNumber,
        roundDate: leagueRoundDate ? new Date(leagueRoundDate).toISOString() : new Date().toISOString(),
        results,
      })
      setMessage(`Həftəlik raund #${leagueRoundNumber} saxlanıldı.`)
      await loadLeagueConfigAndStandings()
    } catch (err: any) {
      setError(err?.response?.data || 'Raund saxlanmadı.')
    }
  }

  const handleFinalizeLeague = async () => {
    resetAlerts()
    if (!selectedTournamentId) return

    const lineupOverrides = leagueStandings.map((s) => ({
      teamId: s.teamId,
      playerIds: (leagueLineupMap[s.teamId] || []).map(Number),
    }))

    try {
      const res = await api.post(`/ratings/leagues/${selectedTournamentId}/finalize`, {
        eventDate: eventDate ? new Date(eventDate).toISOString() : new Date().toISOString(),
        ageCategoryTags: ageTagsText.split(',').map((x) => x.trim()).filter(Boolean),
        lineupOverrides,
      })
      setMessage(res.data?.message || 'League finalize edildi.')
      await loadLeagueConfigAndStandings()
    } catch (err: any) {
      setError(typeof err?.response?.data === 'string' ? err.response.data : (err?.response?.data?.message || 'League finalize alınmadı.'))
    }
  }

  const handleSubmitResults = async () => {
    resetAlerts()
    if (!selectedTournamentId) {
      setError('Turnir seçin.')
      return
    }
    if (parsedRows.length === 0) {
      setError('Əvvəlcə Excel yükləyin.')
      return
    }

    const ranks = buildRankMap()
    const teamsPayload = [] as any[]

    for (let i = 0; i < parsedRows.length; i++) {
      const teamId = rowTeamMap[i]
      const players = rowPlayersMap[i] || []
      if (!teamId) {
        setError(`${i + 1}-ci sətrin komandası map edilməyib.`)
        return
      }
      if (players.length < 4 || players.length > 8) {
        setError(`${parsedRows[i].teamName}: lineup 4-8 oyunçu olmalıdır.`)
        return
      }

      const questionLog = (parsedRows[i].answers || []).map((a: any, qIndex: number) => ({
        questionNumber: qIndex + 1,
        isCorrect: typeof a === 'boolean' ? a : Number(a || 0) > 0,
      }))

      teamsPayload.push({
        teamId,
        rank: ranks[i],
        scoreTotal: Number(parsedRows[i].total || 0),
        questionsTotal: questionCount,
        lineupLocked: true,
        players,
        questionLog,
      })
    }

    try {
      const payload = {
        eventDate: eventDate ? new Date(eventDate).toISOString() : new Date().toISOString(),
        gameType: 'CHGK',
        ageCategoryTags: ageTagsText.split(',').map((x) => x.trim()).filter(Boolean),
        teams: teamsPayload,
      }

      await api.post(`/tournaments/${selectedTournamentId}/results`, payload)
      setMessage('Nəticələr uğurla submit edildi.')
    } catch (err: any) {
      setError(err?.response?.data || 'Nəticələr submit alınmadı.')
    }
  }

  const card = 'bg-white dark:bg-gray-900 rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3'
  const input = 'w-full border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40'

  return (
    <div className="min-h-screen bg-slate-50 pt-16">
      <Helmet>
        <title>Organizer Portal | 3sual.az</title>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        <h1 className="text-2xl font-black text-slate-900">Organizer Portal</h1>

        {message ? <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">{message}</p> : null}
        {error ? <p className="text-sm text-red-600">{String(error)}</p> : null}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <section className={card}>
            <h2 className="font-semibold text-slate-900">Turnir yarat</h2>
            <input className={input} placeholder="Turnir adı" value={newTournamentName} onChange={(e) => setNewTournamentName(e.target.value)} />
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={newTournamentIsLeague} onChange={(e) => setNewTournamentIsLeague(e.target.checked)} />
              Bu turnir liqadır (weekly rounds)
            </label>
            <button onClick={handleCreateTournament} className="bg-slate-900 text-white px-3 py-2 rounded-lg text-sm font-semibold">Yarat</button>
          </section>

          <section className={card}>
            <h2 className="font-semibold text-slate-900">Turniri reytinqə göndər</h2>
            <select className={input} value={selectedTournamentId || ''} onChange={(e) => setSelectedTournamentId(Number(e.target.value))}>
              <option value="">Turnir seçin</option>
              {tournaments.map((t) => (
                <option key={t.id} value={t.id}>
                  #{t.id} {t.name} {t.isLeague ? '[League]' : ''} {t.submissionStatus ? `(${t.submissionStatus})` : ''}
                </option>
              ))}
            </select>
            <button onClick={handleSubmitForRating} className="bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-semibold">Submit for rating</button>
          </section>
        </div>

        {isLeagueSelected ? (
          <section className={card}>
            <h2 className="font-semibold text-slate-900">League automation</h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input className={input} placeholder="Points table: 20,17,15..." value={leaguePointsText} onChange={(e) => setLeaguePointsText(e.target.value)} />
              <input className={input} type="number" min={1} value={leagueRoundNumber} onChange={(e) => setLeagueRoundNumber(Number(e.target.value || 1))} />
              <input className={input} type="datetime-local" value={leagueRoundDate} onChange={(e) => setLeagueRoundDate(e.target.value)} />
              <button onClick={handleSaveLeagueConfig} className="bg-slate-900 text-white px-3 py-2 rounded-lg text-sm font-semibold">Save config</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input className={input} type="datetime-local" value={eventDate} onChange={(e) => setEventDate(e.target.value)} />
              <input className={input} placeholder="Age tags (comma): adult,junior" value={ageTagsText} onChange={(e) => setAgeTagsText(e.target.value)} />
              <input className={input} type="file" accept=".xlsx,.xls" onChange={(e) => handleExcelUpload(e.target.files?.[0] || null)} />
            </div>

            {parsedRows.length > 0 ? (
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-y border-slate-100">
                    <tr>
                      <th className="px-2 py-2 text-left">#</th>
                      <th className="px-2 py-2 text-left">Excel Team</th>
                      <th className="px-2 py-2 text-right">Weekly total</th>
                      <th className="px-2 py-2 text-left">DB Team Map</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.map((r, i) => (
                      <tr key={i} className="border-b border-slate-50 align-top">
                        <td className="px-2 py-2">{i + 1}</td>
                        <td className="px-2 py-2">
                          <div className="font-semibold text-slate-800 dark:text-gray-100">{r.teamName}</div>
                          {r.suggestedTeamName ? <div className="text-slate-500 dark:text-gray-400">Auto: {r.suggestedTeamName}</div> : null}
                        </td>
                        <td className="px-2 py-2 text-right font-bold">{Number(r.total || 0).toFixed(2)}</td>
                        <td className="px-2 py-2">
                          <select className={input} value={rowTeamMap[i] || ''} onChange={(e) => setRowTeamMap((p) => ({ ...p, [i]: e.target.value ? Number(e.target.value) : undefined }))}>
                            <option value="">Komanda seçin</option>
                            {teams.map((t) => <option key={t.id} value={t.id}>{t.name} {t.city ? `(${t.city})` : ''}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            <div className="flex gap-2">
              <button onClick={handleSaveLeagueRound} className="bg-indigo-700 text-white px-3 py-2 rounded-lg text-sm font-semibold">Save weekly round</button>
              <button onClick={loadLeagueConfigAndStandings} className="bg-slate-700 text-white px-3 py-2 rounded-lg text-sm font-semibold">Refresh standings</button>
            </div>

            {leagueStandings.length > 0 ? (
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-y border-slate-100">
                    <tr>
                      <th className="px-2 py-2 text-left">Rank</th>
                      <th className="px-2 py-2 text-left">Team</th>
                      <th className="px-2 py-2 text-right">League points</th>
                      <th className="px-2 py-2 text-left">Final lineup (4-8)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leagueStandings.map((s) => (
                      <tr key={s.teamId} className="border-b border-slate-50 align-top">
                        <td className="px-2 py-2 font-bold">{s.rank}</td>
                        <td className="px-2 py-2">{s.teamName}</td>
                        <td className="px-2 py-2 text-right font-bold">{s.totalPoints}</td>
                        <td className="px-2 py-2">
                          <select
                            multiple
                            className={`${input} min-h-[90px]`}
                            value={(leagueLineupMap[s.teamId] || []).map(String)}
                            onChange={(e) => {
                              const selected = Array.from(e.target.selectedOptions).map((o) => Number(o.value))
                              setLeagueLineupMap((prev) => ({ ...prev, [s.teamId]: selected }))
                            }}
                          >
                            {(teamById[s.teamId]?.players || []).map((p) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            <button onClick={handleFinalizeLeague} className="bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm font-semibold">
              Finalize league and create single rating result
            </button>
          </section>
        ) : (
          <section className={card}>
            <h2 className="font-semibold text-slate-900">Classic tournament: Excel import, team mapping və nəticə submit</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input className={input} type="datetime-local" value={eventDate} onChange={(e) => setEventDate(e.target.value)} />
              <input className={input} placeholder="Age tags (comma): adult,junior" value={ageTagsText} onChange={(e) => setAgeTagsText(e.target.value)} />
              <input className={input} type="file" accept=".xlsx,.xls" onChange={(e) => handleExcelUpload(e.target.files?.[0] || null)} />
            </div>

            {parsedRows.length > 0 ? (
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-y border-slate-100">
                    <tr>
                      <th className="px-2 py-2 text-left">#</th>
                      <th className="px-2 py-2 text-left">Excel Team</th>
                      <th className="px-2 py-2 text-right">Total</th>
                      <th className="px-2 py-2 text-left">DB Team Map</th>
                      <th className="px-2 py-2 text-left">Players (4-8)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.map((r, i) => {
                      const mappedTeamId = rowTeamMap[i]
                      const mappedTeam = mappedTeamId ? teamById[mappedTeamId] : undefined
                      return (
                        <tr key={i} className="border-b border-slate-50 align-top">
                          <td className="px-2 py-2">{i + 1}</td>
                          <td className="px-2 py-2">
                            <div className="font-semibold text-slate-800 dark:text-gray-100">{r.teamName}</div>
                            {r.suggestedTeamName ? <div className="text-slate-500 dark:text-gray-400">Auto: {r.suggestedTeamName}</div> : null}
                          </td>
                          <td className="px-2 py-2 text-right font-bold">{Number(r.total || 0).toFixed(2)}</td>
                          <td className="px-2 py-2">
                            <select
                              className={input}
                              value={mappedTeamId || ''}
                              onChange={(e) => {
                                const next = e.target.value ? Number(e.target.value) : undefined
                                setRowTeamMap((prev) => ({ ...prev, [i]: next }))
                                recalcPlayersByTeam(i, next)
                              }}
                            >
                              <option value="">Komanda seçin</option>
                              {teams.map((t) => <option key={t.id} value={t.id}>{t.name} {t.city ? `(${t.city})` : ''}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-2">
                            {mappedTeam ? (
                              <select
                                multiple
                                className={`${input} min-h-[90px]`}
                                value={(rowPlayersMap[i] || []).map(String)}
                                onChange={(e) => {
                                  const selected = Array.from(e.target.selectedOptions).map((o) => Number(o.value))
                                  setRowPlayersMap((prev) => ({ ...prev, [i]: selected }))
                                }}
                              >
                                {mappedTeam.players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                              </select>
                            ) : <span className="text-slate-400">Əvvəl komanda map edin</span>}
                          </td>
                        </tr>
                      )
                    })}

                    {questionRatings.length > 0 ? (
                      <tr className="bg-amber-50/60">
                        <td colSpan={5} className="px-2 py-2">
                          <span className="font-semibold">Sual reytinqləri:</span> {questionRatings.map((q, idx) => `Q${idx + 1}: ${q ?? '-'}`).join(' | ')}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            ) : null}

            <button onClick={handleSubmitResults} className="bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm font-semibold">Nəticələri submit et</button>
          </section>
        )}
      </div>
    </div>
  )
}

export default OrganizerPortal
